import { Component, OnInit } from '@angular/core';
import { COGNITO_ERROR_NOT_AUTHORIZED_EXCEPTION, COGNITO_ERROR_PASSWORDRESET_REQUIRED_EXCEPTION, COGNITO_ERROR_USER_NOT_CONFIRMED_EXCEPTION, COGNITO_ERROR_USER_NOT_FOUND_EXCEPTION, HTTP_HEADER_AUTHORIZATION, HTTP_HEADER_CONTENT_TYPE, HTTP_HEADER_ESC_TIMEZONE, HTTP_HEADER_ESC_USER_DETAIL, MIME_TYPE_JSON, Path } from '../../utils/constant';
import { ChangePasswordChallengeComponent } from '../change-password-challenge/change-password-challenge.component';
import { AuthenticationDetails, CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { LanguageService } from 'src/app/services/language/language.service';
import { DialogService } from '../../services/dialog/dialog.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { environment } from './../../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService, HelperService } from './../../services';
import { LoginService } from './login.service';
import { catchError } from 'rxjs/operators';
import * as mtz from 'moment-timezone';
import * as _ from 'lodash';
import { of } from 'rxjs';
import { ChangeExpiredPasswordComponent } from '../change-expired-password/change-expired-password.component';
import { Settings } from 'src/app/utils/constant/settings';
import { LockoutService } from 'src/app/services/lockout/lockout.service';

@Component({
  selector: 'esc-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  public loginForm: FormGroup;
  public errorMessage: String = '';
  public isSubmitting: boolean;
  public isPasswordShown: boolean;

  // for lockout duration handling
  private lockoutTimer: any = null;
  private lockoutUsername: string | null = null;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    public helper: HelperService,
    private route: ActivatedRoute,
    public authService: AuthService,
    private loginService: LoginService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    private lockoutService: LockoutService
  ) { }

  ngOnInit() {
    this.loginForm = this.fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
      rememberMe: [false]
    });

    this.route.queryParams.subscribe(params => {
      if (params['username']) {
        this.loginForm.controls['username'].setValue(params['username']);
      }
    });
  }

  startLockoutCountdown(username: string, initialSeconds: number) {
    if (this.lockoutTimer) {
      clearInterval(this.lockoutTimer); // prevent stacked intervals
    }

    this.lockoutUsername = username;
    let secondsLeft = initialSeconds;

    const updateMessage = () => {
      // Only update message if username still matches
      if (this.loginForm.get('username')?.value !== this.lockoutUsername) {
        clearInterval(this.lockoutTimer);
        this.lockoutTimer = null;
        return;
      }

      this.errorMessage = `Too many failed attempts. Please try again in ${secondsLeft} second${secondsLeft > 1 ? 's' : ''}.`;

      if (secondsLeft <= 0) {
        clearInterval(this.lockoutTimer);
        this.lockoutTimer = null;
        this.errorMessage = '';
        return;
      }

      secondsLeft--;
    };

    updateMessage();
    this.lockoutTimer = setInterval(updateMessage, 1000);
  }

  onSubmit(value: any) {
    this.isSubmitting = true;
    this.errorMessage = "";
    this.loginForm.disable();

    let username = _.toUpper(_.trim(value.username));

    let authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: value.password
    });

    let cognitoUser = new CognitoUser({
      Username: username,
      Pool: this.authService.userPool
    });

    let self = this;

    if (this.lockoutTimer) {
      clearInterval(this.lockoutTimer);
      this.lockoutTimer = null;
    }

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async function (session) {
        sessionStorage.setItem("sessionData", "loggedIn");
        await self.authService.initAWSCredentials();

        self.loginService.getUserDetails({
          username: username,
          schema_name: null
        }).pipe(
          catchError((err) => {
            if (environment.showLogs) {
              console.error(err);
            }
            self.isSubmitting = false;
            return of(null);
          })
        )
          .subscribe(res => {
            if (environment.showLogs) {
              console.log("login response: ", res);
            }

            self.helper.storeLocalData(res, username);

            // Trigger condition for password expiry
            if (res.data.modified_date) {
              const modifiedDate = mtz(res.data.modified_date).tz(mtz.tz.guess()).startOf('day');
              const now = mtz().tz(mtz.tz.guess()).startOf('day');
              const diff = now.diff(modifiedDate, 'days');

              // Default password expiry of 90 days if settings not found
              const defaultExpiryDuration = 90;
              let settings: Settings;
              try {
                const storedSettings = localStorage.getItem('settings');
                if (!storedSettings) {
                  throw new Error('No settings found');
                }
                settings = JSON.parse(storedSettings);
                if (!settings?.PASSWORD_EXPIRY_DURATION_IN_DAYS) {
                  throw new Error('Invalid duration value');
                }

                const expiryDays = settings.PASSWORD_EXPIRY_DURATION_IN_DAYS ?? 90;
                const modifiedDate = mtz(res.data.modified_date).tz(mtz.tz.guess()).startOf('day');
                const now = mtz().tz(mtz.tz.guess()).startOf('day');
                const diffInDays = now.diff(modifiedDate, 'days');

                if (environment.showLogs) {
                  console.log('modified date: ', modifiedDate);
                  console.log('now: ', now);
                  console.log('diff in days: ',diffInDays);
                }
                
                const warningDays = 15;
                const remainingDays = (expiryDays - diffInDays) + 1;

                // Show toast if user is in the final warning window (e.g. last 15 days before expiry)
                if (remainingDays <= warningDays && remainingDays > 0) {
                  self.dialogService.confirm({
                    title: "Reminder", 
                    message: `Your account password will expire in ${remainingDays} day(s). Please update it soon.`,
                    hasCancel: false
                  });
                  // self.dialogService.openSnackBar(`Your account password will expire in ${remainingDays} day(s). Please update it soon.`);
                }
              } catch (e) {
                if (environment.showLogs) {
                  console.error('Password expiry check error: ', e);
                  console.error('Could not load password expiry duration from settings, using default:', defaultExpiryDuration);
                }
                settings = { PASSWORD_EXPIRY_DURATION_IN_DAYS: defaultExpiryDuration };
              }

              // // Return a toast message if expiry is near its expiration
              // if (res.data.settings) {
              //   try {
              //     settings = JSON.parse(localStorage.getItem('settings'));
              //     self.dialogService.openSnackBar()
              //   } catch (e) {
              //     if (environment.showLogs) {
              //       console.error('Password expiry check error: ', e);
              //       console.error('Could not parse settings, using default:', defaultExpiryDuration);
              //     }
              //     settings = { PASSWORD_EXPIRY_DURATION_IN_DAYS: defaultExpiryDuration };
              //   }
              // }

              if (diff > settings.PASSWORD_EXPIRY_DURATION_IN_DAYS) {
                self.isSubmitting = false;
                self.authService.logout();
                self.loginForm.enable();
                self.dialogService.openSnackBar(self.languageService.getTranslation("MSG_ERROR_PASSWORD_EXPIRED", {}));

                self.dialogService.openDialog(ChangeExpiredPasswordComponent, {
                  width: "400px",
                  autoFocus: true,
                  disableClose: true,
                  data: {
                    username: username,
                    oldPassword: self.loginForm.get('password')?.value,
                    session: session
                  }
                });

                self.loginForm.get('password').setValue(null);

                return;
              }
            }

            self.isSubmitting = false;
            if (!res || (res.status == 0) || (!res.data)) {
              self.dialogService.openSnackBar(self.languageService.getTranslation("MSG_ERROR_LOADING_WEBTOOL", {}));
              self.loginForm.enable();
              self.authService.logout();
              return;
            }
            // Set a login timestamp on localStorage for idle timeout
            localStorage.setItem('lastActivityTimestamp', new Date().toISOString());
            self.helper.storePermissions(res);
            self.router.navigate(['/']);
          });
      },
      onFailure: function (err) {
        if (environment.showLogs) {
          console.log(err);
        }

        self.isSubmitting = false;
        self.loginForm.enable();

        // const username = self.loginForm.get('username')?.value;

        // // Cognito lockout triggered
        // if (
        //   err.name === COGNITO_ERROR_NOT_AUTHORIZED_EXCEPTION &&
        //   err.message === "Password attempts exceeded"
        // ) {
        //   const username = self.loginForm.get('username')?.value;
        //   let secondsLeft = self.lockoutService.isLocked(username);
        //   if (secondsLeft > 0) {
        //     self.startLockoutCountdown(username, secondsLeft);
        //     return;
        //   }

        //   secondsLeft = self.lockoutService.recordFailure(username);
        //   if (secondsLeft > 0) {
        //     self.startLockoutCountdown(username, secondsLeft);
        //   }

        //   return;
        // }

        // // Increment local failure counter for regular failed attempts
        // if (err.name === COGNITO_ERROR_NOT_AUTHORIZED_EXCEPTION) {
        //   self.lockoutService.recordFailure(username);
        // }

        switch (err.name) {
          case COGNITO_ERROR_NOT_AUTHORIZED_EXCEPTION:
            if (err.message === "Password attempts exceeded") {
              self.errorMessage = "Too many failed attempts. Please try again later.";
            } else {
              self.errorMessage = "Incorrect username or password.";
            }
            break;
          case COGNITO_ERROR_USER_NOT_FOUND_EXCEPTION:
            self.errorMessage = "Incorrect username or password.";
            break;
          case COGNITO_ERROR_PASSWORDRESET_REQUIRED_EXCEPTION:
            self.errorMessage = "Password reset is required for this account.";
            break;
          case COGNITO_ERROR_USER_NOT_CONFIRMED_EXCEPTION:
            self.errorMessage = "Your account has not been verified yet.";
            break;
          default:
            self.errorMessage = "Something went wrong. Please try again later.";
        }
      },
      newPasswordRequired: function (userAttributes, requiredAttributes) {
        self.isSubmitting = false;
        delete userAttributes.phone_number_verified;
        delete userAttributes.email_verified;
        delete userAttributes.email;

        const loadingDialog = self.dialogService.loading();

        self.dialogService.openDialog(ChangePasswordChallengeComponent, {
          width: "400px",
          autoFocus: true,
          disableClose: true,
          data: {
            session: this,
            temporaryPassword: self.loginForm.get('password')?.value 
          }
        })
          .afterClosed()
          .subscribe(async (password) => {
            try {
              if (password) {
                self.isSubmitting = true;

                // Step 1: Complete new password challenge
                await new Promise((resolve, reject) => {
                  cognitoUser.completeNewPasswordChallenge(password.password, userAttributes, {
                    onSuccess: resolve,
                    onFailure: reject
                  });
                });

                // Step 2: Temporarily re-authenticate to update timestamp
                const session: any = await new Promise((resolve, reject) => {
                  const authDetails = new AuthenticationDetails({
                    Username: self.loginForm.get('username').value,
                    Password: password.password
                  });

                  const tempUser = new CognitoUser({
                    Username: self.loginForm.get('username').value,
                    Pool: self.authService.userPool
                  });

                  tempUser.authenticateUser(authDetails, {
                    onSuccess: resolve,
                    onFailure: reject
                  });
                });

                // Step 3: Call API to update last password change date
                const headers = {
                  [HTTP_HEADER_CONTENT_TYPE]: MIME_TYPE_JSON,
                  [HTTP_HEADER_AUTHORIZATION]: session.getIdToken().getJwtToken(),
                  [HTTP_HEADER_ESC_USER_DETAIL]: localStorage.getItem("userDetails"),
                  [HTTP_HEADER_ESC_TIMEZONE]: localStorage.getItem("escTimezone")
                };

                await fetch(
                  environment.api.endpoints[0].endpoint + Path.LOGIN.UPDATE_LAST_CHANGE_PASSWORD_DATE,
                  {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({})
                  }
                );

                await self.authService.initAWSCredentials();

                // login immediately after changing temp password successfully
                self.loginService.getUserDetails({
                  username: username,
                  schema_name: null
                }).subscribe({
                  next: (res) => {
                    // Set a login timestamp on localStorage for idle timeout
                    localStorage.setItem('lastActivityTimestamp', new Date().toISOString());
                    self.helper.storeLocalData(res, username);
                    self.helper.storePermissions(res);
                    self.router.navigate(['/']);
                  },
                  error: (err) => {
                    self.dialogService.openSnackBar(self.languageService.getTranslation("MSG_ERROR_LOADING_WEBTOOL", {}));
                    if (environment.showLogs) {
                      self.authService.logout();
                      console.error(err);
                    }
                  }
                });
              }
            } catch (err) {
              console.error('Password update error:', err);
              self.dialogService.openSnackBar(_.get(err, 'message', 'Unexpected error occurred.'));
              self.authService.logout(); // Always logout regardless of error
            } finally {
              // Always reset the login form
              self.loginForm.get('password').setValue(null);
              self.loginForm.enable();
              self.isSubmitting = false;
              loadingDialog.close();
            }
          });

        loadingDialog.close();
      }
    })
  }
}

